import {createContext, useContext} from 'react'
import {openAlpinistPanel} from '@shared/hooks/useAlpinistUrlSync'

/**
 * Opens another of the user's conversations in the chat that renders it
 * (AB#6775: links to earlier conversations Alpinist searched or read).
 *
 * AgentChat provides its own switcher, so a link in the full-page chat or the
 * side panel just changes that chat's active conversation. Chats without one
 * (the incident panel) fall back to opening it in the Alpinist side panel.
 */
export const OpenConversationContext =
  createContext<(conversationId: string) => void>(openAlpinistPanel)

export const useOpenConversation = (): ((conversationId: string) => void) =>
  useContext(OpenConversationContext)

/**
 * Alpinist links an earlier conversation in its answer as
 * `[title](alpinist-conversation:<uuid>)` (AB#6775).
 */
export const CONVERSATION_LINK_SCHEME = 'alpinist-conversation'

const CONVERSATION_HREF =
  /^alpinist-conversation:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/** Whether an href uses the conversation scheme at all, well-formed or not. */
export const isConversationHref = (href: string | undefined): boolean =>
  !!href && href.toLowerCase().startsWith(`${CONVERSATION_LINK_SCHEME}:`)

/** The conversation id of a well-formed conversation link, else null. */
export const conversationIdFromHref = (href: string | undefined): string | null =>
  href?.match(CONVERSATION_HREF)?.[1] ?? null
